import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { QrCodeIcon, ArrowLeft } from 'lucide-react';

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center space-x-2 text-gray-600 hover:text-gray-900">
              <ArrowLeft className="h-5 w-5" />
              <span>Back to Home</span>
            </Link>
            <Link to="/" className="flex items-center space-x-2">
              <QrCodeIcon className="h-8 w-8 text-blue-600" />
              <span className="text-2xl font-bold text-gray-900">iComment.app</span>
            </Link>
          </div>
        </div>
      </header>

      {/* Privacy Policy Content */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        <Card>
          <CardContent className="p-8 prose prose-blue max-w-none">
            <h1 className="text-4xl font-bold text-gray-900 mb-2">Privacy Policy</h1>
            <p className="text-gray-600 mb-8">Last Updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Introduction</h2>
              <p className="text-gray-700 leading-relaxed">
                Welcome to iComment.app ("we," "our," or "us"). We are committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our anonymous two-way messaging service through QR codes.
              </p>
              <p className="text-gray-700 leading-relaxed mt-4">
                By using iComment.app, you agree to the collection and use of information in accordance with this policy. If you do not agree with our policies and practices, please do not use our service.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Information We Collect</h2>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-3">2.1 Information You Provide</h3>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li><strong>Account Information:</strong> Email address, username, password, and subscription details</li>
                <li><strong>Contact Information:</strong> Phone numbers and email addresses you configure for notifications</li>
                <li><strong>Messages:</strong> Content of messages you send and receive through our platform</li>
                <li><strong>QR Code Data:</strong> Titles, descriptions, and custom settings for your QR codes</li>
                <li><strong>Payment Information:</strong> Processed securely through our payment processor; we do not store credit card details</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">2.2 Automatically Collected Information</h3>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li><strong>Usage Data:</strong> QR code scans, message timestamps, and feature usage</li>
                <li><strong>Device Information:</strong> Browser type, device type, IP address, and operating system</li>
                <li><strong>Location Data:</strong> Optional location information when finders choose to share it</li>
                <li><strong>Cookies and Tracking:</strong> We use cookies to maintain your session and improve user experience</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">3. SMS Messaging Consent & Opt-Out</h2>
              
              <div className="bg-blue-50 border-l-4 border-blue-600 p-6 mb-4">
                <h3 className="text-xl font-semibold text-blue-900 mb-3">SMS Consent</h3>
                <p className="text-gray-800 leading-relaxed mb-4">
                  By providing your phone number and enabling SMS notifications for your QR codes, you expressly consent to receive text messages from iComment.app. These messages may include:
                </p>
                <ul className="list-disc pl-6 text-gray-800 space-y-2">
                  <li>Notifications when someone scans your QR code and sends you a message</li>
                  <li>Replies to messages you've sent</li>
                  <li>Account updates and service notifications</li>
                  <li>Usage limit warnings and subscription reminders</li>
                </ul>
              </div>

              <div className="bg-green-50 border-l-4 border-green-600 p-6 mb-4">
                <h3 className="text-xl font-semibold text-green-900 mb-3">How to Opt-Out</h3>
                <p className="text-gray-800 leading-relaxed mb-4">
                  You have the right to opt-out of SMS messages at any time:
                </p>
                <ul className="list-disc pl-6 text-gray-800 space-y-2">
                  <li><strong>Reply "STOP":</strong> Text "STOP" to any SMS you receive from us to unsubscribe immediately</li>
                  <li><strong>Dashboard Settings:</strong> Log in to your account and disable SMS notifications in your notification settings</li>
                  <li><strong>Per QR Code:</strong> Manage notification preferences individually for each QR code</li>
                  <li><strong>Contact Support:</strong> Email us at support@icomment.app to disable SMS notifications</li>
                </ul>
                <p className="text-gray-800 leading-relaxed mt-4">
                  After opting out, you will no longer receive SMS notifications, but you will continue to receive email notifications (unless you also opt-out of those).
                </p>
              </div>

              <div className="bg-yellow-50 border-l-4 border-yellow-600 p-6">
                <h3 className="text-xl font-semibold text-yellow-900 mb-3">SMS Frequency & Charges</h3>
                <ul className="list-disc pl-6 text-gray-800 space-y-2">
                  <li><strong>Frequency:</strong> Message frequency varies based on activity. You may receive multiple messages per day if your QR codes are actively scanned</li>
                  <li><strong>Charges:</strong> Message and data rates may apply. Contact your mobile carrier for details</li>
                  <li><strong>Availability:</strong> SMS notifications are only available for Basic and Premium subscription plans</li>
                  <li><strong>Carrier Support:</strong> Service available on major US carriers. Delivery times may vary</li>
                </ul>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">4. How We Use Your Information</h2>
              <p className="text-gray-700 leading-relaxed mb-4">We use your information to:</p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>Provide and maintain our messaging service</li>
                <li>Send notifications when your QR codes receive messages</li>
                <li>Process payments and manage subscriptions</li>
                <li>Improve and personalize your experience</li>
                <li>Respond to customer support inquiries</li>
                <li>Detect and prevent fraud or abuse</li>
                <li>Send important service updates and security alerts</li>
                <li>Analyze usage patterns to improve our service</li>
                <li>Comply with legal obligations</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Information Sharing and Disclosure</h2>
              <p className="text-gray-700 leading-relaxed mb-4">We do not sell your personal information. We may share your information in the following circumstances:</p>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-3">5.1 With Your Consent</h3>
              <p className="text-gray-700 leading-relaxed">
                When you choose to share your contact information with message senders (finders), that information is shared to facilitate communication.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-4">5.2 Service Providers</h3>
              <p className="text-gray-700 leading-relaxed mb-2">We work with third-party service providers who assist us in:</p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li><strong>Email Delivery:</strong> SendGrid for email notifications</li>
                <li><strong>SMS Delivery:</strong> Twilio for text message notifications</li>
                <li><strong>Payment Processing:</strong> Stripe or PayPal for subscription payments</li>
                <li><strong>Cloud Hosting:</strong> For secure data storage and service delivery</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-4">5.3 Legal Requirements</h3>
              <p className="text-gray-700 leading-relaxed">
                We may disclose your information if required by law, court order, or government regulation, or to protect our rights, property, or safety.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-4">5.4 Business Transfers</h3>
              <p className="text-gray-700 leading-relaxed">
                If we are involved in a merger, acquisition, or sale of assets, your information may be transferred as part of that transaction.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Data Security</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                We implement industry-standard security measures to protect your information:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>Encryption of data in transit (HTTPS/TLS)</li>
                <li>Encrypted password storage</li>
                <li>Regular security audits and monitoring</li>
                <li>Access controls and authentication</li>
                <li>Secure data centers with physical security</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                While we strive to protect your information, no method of transmission over the internet is 100% secure. We cannot guarantee absolute security.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Your Privacy Rights</h2>
              <p className="text-gray-700 leading-relaxed mb-4">You have the following rights regarding your personal information:</p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li><strong>Access:</strong> Request a copy of your personal data</li>
                <li><strong>Correction:</strong> Update or correct inaccurate information</li>
                <li><strong>Deletion:</strong> Request deletion of your account and associated data</li>
                <li><strong>Opt-Out:</strong> Unsubscribe from marketing emails and SMS messages</li>
                <li><strong>Data Portability:</strong> Request your data in a portable format</li>
                <li><strong>Restrict Processing:</strong> Limit how we use your information</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                To exercise these rights, please contact us at privacy@icomment.app or through your account settings.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Data Retention</h2>
              <p className="text-gray-700 leading-relaxed">
                We retain your information for as long as your account is active or as needed to provide services. When you delete your account:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2 mt-4">
                <li>Your QR codes and messages are permanently deleted within 30 days</li>
                <li>Billing information is retained for tax and accounting purposes (up to 7 years)</li>
                <li>Backup copies may be retained for up to 90 days</li>
                <li>Anonymous usage statistics may be retained indefinitely</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Children's Privacy</h2>
              <p className="text-gray-700 leading-relaxed">
                Our service is not intended for children under 13 years of age. We do not knowingly collect personal information from children. If you believe we have collected information from a child, please contact us immediately at privacy@icomment.app.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">10. International Users</h2>
              <p className="text-gray-700 leading-relaxed">
                Our services are operated from the United States. If you are accessing our service from outside the US, please be aware that your information may be transferred to, stored, and processed in the United States where our servers are located and our central database is operated.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Cookies and Tracking Technologies</h2>
              <p className="text-gray-700 leading-relaxed mb-4">We use cookies and similar technologies to:</p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>Maintain your logged-in session</li>
                <li>Remember your preferences</li>
                <li>Analyze site traffic and usage</li>
                <li>Improve site functionality</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                You can control cookies through your browser settings. Disabling cookies may affect site functionality.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Changes to This Privacy Policy</h2>
              <p className="text-gray-700 leading-relaxed">
                We may update this Privacy Policy from time to time. We will notify you of any changes by:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2 mt-4">
                <li>Posting the new Privacy Policy on this page</li>
                <li>Updating the "Last Updated" date</li>
                <li>Sending you an email notification (for material changes)</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                Your continued use of our service after changes are posted constitutes acceptance of the updated policy.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">13. Contact Us</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                If you have questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:
              </p>
              <div className="bg-gray-100 p-6 rounded-lg">
                <p className="text-gray-900 font-semibold mb-2">iComment.app</p>
                <p className="text-gray-700">Email: privacy@icomment.app</p>
                <p className="text-gray-700">Support: support@icomment.app</p>
                <p className="text-gray-700 mt-4">
                  <Link to="/contact" className="text-blue-600 hover:text-blue-800 underline">
                    Contact Form
                  </Link>
                </p>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">14. California Privacy Rights (CCPA)</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                California residents have additional rights under the California Consumer Privacy Act (CCPA):
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>Right to know what personal information is collected</li>
                <li>Right to know if personal information is sold or disclosed</li>
                <li>Right to say no to the sale of personal information</li>
                <li>Right to access your personal information</li>
                <li>Right to equal service and price, even if you exercise your privacy rights</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                <strong>Note:</strong> We do not sell personal information.
              </p>
            </section>

            <div className="bg-blue-100 border border-blue-300 rounded-lg p-6 mt-8">
              <p className="text-blue-900 font-semibold mb-2">Your Privacy Matters</p>
              <p className="text-blue-800">
                We are committed to protecting your privacy and handling your data responsibly. If you have any questions or concerns, please don't hesitate to reach out to us.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8 mt-12">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className="text-gray-400">
            © {new Date().getFullYear()} iComment.app - All rights reserved
          </p>
          <div className="mt-4 space-x-4">
            <Link to="/terms" className="text-gray-400 hover:text-white">Terms of Service</Link>
            <Link to="/privacy-policy" className="text-gray-400 hover:text-white">Privacy Policy</Link>
            <Link to="/contact" className="text-gray-400 hover:text-white">Contact</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
